import { FilterDefinition } from "src/uniform/contenttypes/filterDefinition";
import { SortDefinition } from "src/uniform/contenttypes/sortDefinition";

import { FilterOptions, SubFilterObject, UniformFilterObject } from "./types";

export type OptionPair = { value: string; label: string };

type OptionItem = string | { value: string; label?: string };

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function isPrimitive(v: unknown): v is string | number | boolean {
    return v !== null && v !== undefined && typeof v !== "object";
}

function isUnresolvedLink(value: unknown): boolean {
    if (typeof value !== "object" || value === null) return false;
    const sys = (value as Record<string, unknown>)["sys"];
    return (
        typeof sys === "object" &&
        sys !== null &&
        (sys as Record<string, unknown>)["type"] === "Link"
    );
}

function coerceToPrimitiveArray(value: unknown): string[] {
    const values = Array.isArray(value) ? value : [value];
    return values.filter(isPrimitive).map(String);
}

function normaliseOptionItems(
    options: string | OptionItem[] | null | undefined
): OptionItem[] {
    if (!options) return [];
    if (Array.isArray(options)) return options;
    try {
        const parsed = JSON.parse(options);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

function optionItemToPair(o: OptionItem): OptionPair {
    return typeof o === "string"
        ? { value: o, label: o }
        : { value: String(o.value), label: o.label ?? String(o.value) };
}

// ---------------------------------------------------------------------------
// resolveFieldPath
// ---------------------------------------------------------------------------

/**
 * Resolves a dot-path against a data item. Supports array spreads via `[]`
 * (e.g. "fields.category[].fields.urlSlug"). Returns `undefined` for missing
 * or unresolved Contentful Link nodes; never throws.
 */
export function resolveFieldPath(
    item: unknown,
    path: string
): unknown | unknown[] {
    const segments = path.split(/\[\]\.?/);

    function walk(value: unknown, segs: string[]): unknown | unknown[] {
        if (segs.length === 0) return value;

        const [head, ...tail] = segs;
        if (head === undefined) return value;

        let current: unknown = value;

        for (const key of head.split(".").filter(Boolean)) {
            if (current === null || current === undefined) return undefined;
            if (typeof current !== "object") return undefined;
            if (isUnresolvedLink(current)) return undefined;
            current = (current as Record<string, unknown>)[key];
        }

        if (tail.length === 0) return current;
        if (!Array.isArray(current)) return undefined;

        const results = current.flatMap((el) => {
            const resolved = walk(el, tail);
            return Array.isArray(resolved) ? resolved : [resolved];
        });

        return results.filter((v) => v !== undefined);
    }

    return walk(item, segments);
}

// ---------------------------------------------------------------------------
// Option parsing (explicit override)
// ---------------------------------------------------------------------------

/** Parses FilterDefinition.options into value strings. Accepts a pre-parsed array or JSON string. */
export function parseOptions(
    options: string | OptionItem[] | null | undefined
): string[] {
    return normaliseOptionItems(options).map((o) =>
        typeof o === "string" ? o : String(o.value)
    );
}

/** Parses FilterDefinition.options into { value, label } pairs. */
export function parseOptionsWithLabels(
    options: string | OptionItem[] | null | undefined
): OptionPair[] {
    return normaliseOptionItems(options).map(optionItemToPair);
}

// ---------------------------------------------------------------------------
// Option derivation (auto-derive from items)
// ---------------------------------------------------------------------------

/** Collects unique primitive values at `fieldPath` across all items, sorted alphabetically. */
export function deriveOptions(items: unknown[], fieldPath: string): string[] {
    const seen = new Set<string>();
    for (const item of items) {
        for (const v of coerceToPrimitiveArray(
            resolveFieldPath(item, fieldPath)
        )) {
            seen.add(v);
        }
    }
    return Array.from(seen).sort((a, b) => a.localeCompare(b));
}

/**
 * Like `deriveOptions`, but pairs each value with a display label from a
 * parallel `labelFieldPath`. Falls back to the value when the label doesn't resolve.
 */
export function deriveOptionsWithLabels(
    items: unknown[],
    fieldPath: string,
    labelFieldPath: string
): OptionPair[] {
    const seen = new Map<string, string>();

    for (const item of items) {
        const valueArr = coerceToPrimitiveArray(
            resolveFieldPath(item, fieldPath)
        );
        const labelArr = coerceToPrimitiveArray(
            resolveFieldPath(item, labelFieldPath)
        );

        valueArr.forEach((valueStr, i) => {
            if (!seen.has(valueStr)) {
                seen.set(valueStr, labelArr[i] ?? valueStr);
            }
        });
    }

    return Array.from(seen.entries())
        .map(([value, label]) => ({ value, label }))
        .sort((a, b) => a.label.localeCompare(b.label));
}

// ---------------------------------------------------------------------------
// Shared resolution (used by both buildUniformFilterObject and buildLabelMaps)
// ---------------------------------------------------------------------------

function resolveOptionPairs(
    items: unknown[],
    filter: FilterDefinition
): OptionPair[] {
    if (filter.options) {
        const pairs = parseOptionsWithLabels(filter.options);
        if (pairs.length > 0) return pairs;
    }
    if (filter.labelFieldPath) {
        return deriveOptionsWithLabels(
            items,
            filter.fieldPath,
            filter.labelFieldPath
        );
    }
    return deriveOptions(items, filter.fieldPath).map((v) => ({
        value: v,
        label: v
    }));
}

// ---------------------------------------------------------------------------
// State initialisation
// ---------------------------------------------------------------------------

/** Builds the initial UniformFilterObject from FilterDefinitions + items. All options start inactive. */
export function buildUniformFilterObject(
    items: unknown[],
    filters: FilterDefinition[]
): UniformFilterObject {
    return Object.fromEntries(
        filters.map((filter) => {
            const subFilter: SubFilterObject = Object.fromEntries(
                resolveOptionPairs(items, filter).map(({ value }) => [
                    value,
                    {
                        committedActive: false,
                        stagedActive: false
                    } satisfies FilterOptions
                ])
            );
            return [filter.id, subFilter];
        })
    );
}

/** Builds a filterId → { value: displayLabel } map for all filters. */
export function buildLabelMaps(
    items: unknown[],
    filters: FilterDefinition[]
): Record<string, Record<string, string>> {
    return Object.fromEntries(
        filters.map((filter) => [
            filter.id,
            Object.fromEntries(
                resolveOptionPairs(items, filter).map(({ value, label }) => [
                    value,
                    label
                ])
            )
        ])
    );
}

// ---------------------------------------------------------------------------
// Filter pipeline
// ---------------------------------------------------------------------------

function matchValue(
    itemValue: unknown,
    matchType: FilterDefinition["matchType"],
    selected: string[]
): boolean {
    const itemValues = coerceToPrimitiveArray(itemValue);

    if (matchType === "equals") {
        return selected.some((s) => itemValues.includes(s));
    }
    if (matchType === "includes") {
        return selected.every((s) => itemValues.includes(s));
    }
    if (matchType === "contains") {
        return selected.some((s) =>
            itemValues.some((v) => v.toLowerCase().includes(s.toLowerCase()))
        );
    }
    return false;
}

/** Returns items that pass every active filter. Filters with no committed selection are pass-through. */
export function applyFilters(
    items: unknown[],
    filters: FilterDefinition[],
    filterState: UniformFilterObject
): unknown[] {
    const activeFilters = filters.filter((filter) => {
        const subFilter = filterState[filter.id];
        return (
            subFilter &&
            Object.values(subFilter).some((opt) => opt.committedActive)
        );
    });

    if (activeFilters.length === 0) return items;

    return items.filter((item) =>
        activeFilters.every((filter) => {
            const subFilter = filterState[filter.id];
            if (!subFilter) return true;
            const selected = Object.entries(subFilter)
                .filter(([, opt]) => opt.committedActive)
                .map(([key]) => key);
            return matchValue(
                resolveFieldPath(item, filter.fieldPath),
                filter.matchType,
                selected
            );
        })
    );
}

/** Case-insensitive substring search across one or more field paths. No-op when no paths or empty query. */
export function applySearch(
    items: unknown[],
    searchFieldPaths: string[] | undefined,
    query: string
): unknown[] {
    if (!searchFieldPaths || searchFieldPaths.length === 0 || !query.trim()) {
        return items;
    }

    const normalised = query.trim().toLowerCase();

    return items.filter((item) =>
        searchFieldPaths.some((path) => {
            const values = coerceToPrimitiveArray(resolveFieldPath(item, path));
            return values.some((v) => v.toLowerCase().includes(normalised));
        })
    );
}

/** Returns a new sorted array. No-op when sort is undefined. */
export function applySort(
    items: unknown[],
    sort: SortDefinition | undefined
): unknown[] {
    if (!sort) return items;

    const direction = sort.direction === "desc" ? -1 : 1;
    const valueType = sort.valueType ?? "string";

    return [...items].sort((a, b) => {
        const av = coerceForSort(
            resolveFieldPath(a, sort.fieldPath),
            valueType
        );
        const bv = coerceForSort(
            resolveFieldPath(b, sort.fieldPath),
            valueType
        );

        if (av === undefined && bv === undefined) return 0;
        if (av === undefined) return 1;
        if (bv === undefined) return -1;

        if (av < bv) return -1 * direction;
        if (av > bv) return 1 * direction;
        return 0;
    });
}

function coerceForSort(
    value: unknown,
    valueType: "string" | "number" | "date"
): string | number | undefined {
    const first = Array.isArray(value) ? value[0] : value;
    if (first === null || first === undefined) return undefined;

    if (valueType === "number") {
        const n = typeof first === "number" ? first : Number(first);
        return Number.isNaN(n) ? undefined : n;
    }

    if (valueType === "date") {
        const ms = new Date(String(first)).getTime();
        return Number.isNaN(ms) ? undefined : ms;
    }

    return String(first).toLowerCase();
}
